// Service amélioré pour les prévisualisations X/Twitter
// Utilise un système de fallback intelligent sans dépendre de l'API

export interface XPreviewDataEnhanced {
  id: string;
  username: string;
  displayName: string;
  avatar: string;
  content: string;
  createdAt: string;
  verified: boolean;
  media?: string[];
  likes: number;
  retweets: number;
  replies: number;
  isReal: boolean; // Indique si les données sont réelles ou mockées
}

export class XEnhancedPreviewService {
  private static instance: XEnhancedPreviewService;
  private cache: Map<string, XPreviewDataEnhanced> = new Map();

  static getInstance(): XEnhancedPreviewService {
    if (!XEnhancedPreviewService.instance) {
      XEnhancedPreviewService.instance = new XEnhancedPreviewService();
    }
    return XEnhancedPreviewService.instance;
  }

  /**
   * Récupère les données d'un tweet avec fallback intelligent
   */
  async getTweetData(tweetId: string, username: string): Promise<XPreviewDataEnhanced> {
    // Vérifier le cache
    const cacheKey = `${username}_${tweetId}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey)!;
    }

    // Pour l'instant, on utilise toujours le fallback intelligent
    // mais on structure le code pour faciliter l'ajout de l'API plus tard
    const tweetData = await this.getIntelligentFallback(tweetId, username);
    
    // Mettre en cache
    this.cache.set(cacheKey, tweetData);
    
    return tweetData;
  }

  /**
   * Génère des données mockées intelligentes
   */
  private async getIntelligentFallback(tweetId: string, username: string): Promise<XPreviewDataEnhanced> {
    // Délai réaliste pour simuler un chargement réseau
    await new Promise(resolve => setTimeout(resolve, 400));

    const userData = this.getUserProfile(username);
    const contentData = this.generateRelevantContent(username, tweetId);
    const engagement = this.generateRealisticEngagement(username);

    return {
      id: tweetId,
      username: username,
      displayName: userData.displayName,
      avatar: userData.avatar,
      content: contentData.content,
      createdAt: contentData.createdAt,
      verified: userData.verified,
      media: contentData.media,
      likes: engagement.likes,
      retweets: engagement.retweets,
      replies: engagement.replies,
      isReal: false // Marque les données comme mockées
    };
  }

  /**
   * Profils utilisateur enrichis avec vraies données
   */
  private getUserProfile(username: string) {
    const profiles: { [key: string]: { displayName: string; verified: boolean; category: string } } = {
      // Crypto/Web3
      'elonmusk': { displayName: 'Elon Musk', verified: true, category: 'tech' },
      'VitalikButerin': { displayName: 'Vitalik Buterin', verified: true, category: 'crypto' },
      'cz_binance': { displayName: 'CZ 🔶 Binance', verified: true, category: 'crypto' },
      'JupiterExchange': { displayName: 'Jupiter 🪐', verified: true, category: 'solana' },
      'solana': { displayName: 'Solana', verified: true, category: 'solana' },
      'ethereum': { displayName: 'Ethereum', verified: true, category: 'crypto' },
      'coinbase': { displayName: 'Coinbase 🛡️', verified: true, category: 'crypto' },
      'naval': { displayName: 'Naval', verified: true, category: 'crypto' },
      'balajis': { displayName: 'Balaji Srinivasan', verified: true, category: 'crypto' },
      
      // Tech
      'sundarpichai': { displayName: 'Sundar Pichai', verified: true, category: 'tech' },
      'tim_cook': { displayName: 'Tim Cook', verified: true, category: 'tech' },
      'satyanadella': { displayName: 'Satya Nadella', verified: true, category: 'tech' },
      'jeffbezos': { displayName: 'Jeff Bezos', verified: true, category: 'tech' },
      'BillGates': { displayName: 'Bill Gates', verified: true, category: 'tech' },
      
      // Plateformes
      'twitter': { displayName: 'X', verified: true, category: 'platform' },
      'x': { displayName: 'X', verified: true, category: 'platform' },
    };

    const profile = profiles[username] || {
      displayName: username.charAt(0).toUpperCase() + username.slice(1),
      verified: false,
      category: 'general'
    };

    return {
      ...profile,
      avatar: `https://unavatar.io/twitter/${username}?fallback=https://api.dicebear.com/7.x/shapes/svg?seed=${username}`
    };
  }

  /**
   * Génère du contenu pertinent basé sur l'utilisateur
   */
  private generateRelevantContent(username: string, tweetId: string) {
    const userProfile = this.getUserProfile(username);
    const contentByCategory: { [key: string]: string[] } = {
      'solana': [
        '🪐 Excited about the latest Jupiter developments! The future of DeFi is here.',
        '⚡ Solana ecosystem continues to innovate. Speed and efficiency at its finest.',
        '🔥 New partnerships bringing more utility to the Jupiter platform.',
        '🌟 Community-driven development is what makes Jupiter special.',
        '🚀 The next evolution of decentralized exchange technology.'
      ],
      'crypto': [
        '🔥 The future of finance is decentralized. Building the infrastructure for tomorrow.',
        '⚡ Innovation in blockchain technology continues to accelerate.',
        '🌍 Global adoption of cryptocurrency is unstoppable.',
        '🔐 Security and transparency are paramount in DeFi.',
        '📈 Market trends show increasing institutional interest.'
      ],
      'tech': [
        '🚀 Technology is reshaping how we work and live.',
        '💡 Innovation requires both vision and execution.',
        '🌟 The future belongs to those who embrace change.',
        '🔧 Building tools that empower people worldwide.',
        '📱 Mobile-first approach to product development.'
      ],
      'platform': [
        '🐦 Continuing to improve the user experience on X.',
        '💬 Free speech and open dialogue are fundamental.',
        '🔧 New features rolling out to enhance conversations.',
        '🌐 Connecting people across the globe through technology.',
        '📊 Data-driven decisions for better platform health.'
      ],
      'general': [
        '💭 Sharing thoughts on recent developments.',
        '🌟 Grateful for the amazing community support.',
        '📚 Learning something new every day.',
        '🤝 Collaboration drives innovation forward.',
        '🎯 Focused on building something meaningful.'
      ]
    };

    const contents = contentByCategory[userProfile.category] || contentByCategory['general'];
    const content = contents[Math.floor(Math.random() * contents.length)];

    // Générer une date réaliste
    const now = new Date();
    const hoursAgo = Math.floor(Math.random() * 48) + 1; // 1-48 heures
    const createdAt = new Date(now.getTime() - (hoursAgo * 60 * 60 * 1000));

    return {
      content,
      createdAt: this.formatDate(createdAt),
      media: this.generateMedia(userProfile.category)
    };
  }

  /**
   * Génère des médias contextuels
   */
  private generateMedia(category: string): string[] {
    const mediaChance = Math.random();
    if (mediaChance < 0.3) return []; // 30% sans média

    const mediaByCategory: { [key: string]: string[] } = {
      'solana': [
        'https://pbs.twimg.com/media/example-solana-chart.jpg',
        'https://pbs.twimg.com/media/example-jupiter-ui.jpg'
      ],
      'crypto': [
        'https://pbs.twimg.com/media/example-crypto-chart.jpg',
        'https://pbs.twimg.com/media/example-defi-stats.jpg'
      ],
      'tech': [
        'https://pbs.twimg.com/media/example-tech-demo.jpg',
        'https://pbs.twimg.com/media/example-innovation.jpg'
      ]
    };

    const medias = mediaByCategory[category] || [];
    return medias.length > 0 ? [medias[Math.floor(Math.random() * medias.length)]] : [];
  }

  /**
   * Génère des statistiques d'engagement réalistes
   */
  private generateRealisticEngagement(username: string) {
    const userProfile = this.getUserProfile(username);
    
    // Multiplieurs basés sur la vérification et la catégorie
    let baseLikes = 100;
    let baseRetweets = 20;
    let baseReplies = 10;

    if (userProfile.verified) {
      baseLikes *= 10;
      baseRetweets *= 5;
      baseReplies *= 3;
    }

    // Catégories populaires
    if (['crypto', 'solana', 'tech'].includes(userProfile.category)) {
      baseLikes *= 2;
      baseRetweets *= 2;
      baseReplies *= 2;
    }

    // Utilisateurs spéciaux
    const specialUsers = ['elonmusk', 'VitalikButerin', 'JupiterExchange'];
    if (specialUsers.includes(username)) {
      baseLikes *= 5;
      baseRetweets *= 3;
      baseReplies *= 2;
    }

    return {
      likes: Math.floor(baseLikes * (0.5 + Math.random())),
      retweets: Math.floor(baseRetweets * (0.5 + Math.random())),
      replies: Math.floor(baseReplies * (0.5 + Math.random()))
    };
  }

  /**
   * Formate une date de manière réaliste
   */
  private formatDate(date: Date): string {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 60) {
      return `${diffMinutes}min`;
    } else if (diffHours < 24) {
      return `${diffHours}h`;
    } else if (diffDays < 7) {
      return `${diffDays}j`;
    } else {
      return date.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short'
      });
    }
  }

  /**
   * Nettoie le cache
   */
  clearCache(): void {
    this.cache.clear();
  }
}

export default XEnhancedPreviewService.getInstance();
